import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import MedicationForm from '../components/medications/MedicationForm'

const todayISO = () => new Date().toLocaleDateString('en-CA')
const STATUSES = [
  { value: 'taken', label: 'Taken', cls: 'badge-green', btn: 'var(--green)' },
  { value: 'missed', label: 'Missed', cls: 'badge-amber', btn: 'var(--amber)' },
  { value: 'refused', label: 'Refused', cls: 'badge-danger', btn: 'var(--danger)' },
]
const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })

function refillState(date, today) {
  if (!date) return null
  if (date < today) return { label: 'Refill overdue', cls: 'badge-danger' }
  const in7 = new Date(); in7.setDate(in7.getDate() + 7)
  if (date <= in7.toLocaleDateString('en-CA')) return { label: 'Refill due soon', cls: 'badge-amber' }
  return null
}

export default function MedicationsPage() {
  const { user } = useAuth()
  const { recipient, role } = useCare()
  const toast = useToast()
  const navigate = useNavigate()
  const canManage = role === 'admin' || role === 'family_member'

  const [tab, setTab] = useState('today')
  const [meds, setMeds] = useState([])
  const [todayLogs, setTodayLogs] = useState({}) // medId -> status
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null) // { medication? } | null
  const [filterMed, setFilterMed] = useState('all')
  const today = todayISO()

  const load = useCallback(async () => {
    if (!recipient) return
    const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30)
    const [medRes, todayRes, histRes] = await Promise.all([
      supabase.from('medications').select('*').eq('care_recipient_id', recipient.id).eq('is_active', true).order('name'),
      supabase.from('medication_logs').select('medication_id, status').eq('care_recipient_id', recipient.id).eq('log_date', today),
      supabase.from('medication_logs').select('*, medication:medications(name)').eq('care_recipient_id', recipient.id).gte('log_date', monthAgo.toLocaleDateString('en-CA')).order('log_date', { ascending: false }).order('created_at', { ascending: false }),
    ])
    setMeds(medRes.data || [])
    const map = {}
    for (const r of todayRes.data || []) map[r.medication_id] = r.status
    setTodayLogs(map)
    setHistory(histRes.data || [])
    setLoading(false)
  }, [recipient, today])

  useEffect(() => { load() }, [load])

  // Realtime: med log statuses sync across family.
  useEffect(() => {
    if (!recipient) return
    const ch = supabase
      .channel(`med-logs-${recipient.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'medication_logs', filter: `care_recipient_id=eq.${recipient.id}` }, () => load())
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [recipient, load])

  async function setStatus(medId, status) {
    const prev = todayLogs[medId]
    setTodayLogs((m) => ({ ...m, [medId]: status })) // optimistic
    const { error } = await supabase
      .from('medication_logs')
      .upsert(
        { medication_id: medId, care_recipient_id: recipient.id, logged_by: user.id, log_date: today, status },
        { onConflict: 'medication_id,log_date' }
      )
    if (error) {
      setTodayLogs((m) => ({ ...m, [medId]: prev })) // rollback
      toast.error(humanizeError(error))
    }
  }

  async function deactivate(med) {
    if (!window.confirm(`Deactivate ${med.name}? It stays in history.`)) return
    const { error } = await supabase.from('medications').update({ is_active: false }).eq('id', med.id)
    if (error) return toast.error(humanizeError(error))
    setMeds((m) => m.filter((x) => x.id !== med.id))
    toast.success('Medication deactivated.')
  }

  if (!recipient || loading) return <LoadingSpinner label="Loading medications…" />

  const loggedCount = Object.keys(todayLogs).length
  const filteredHistory = filterMed === 'all' ? history : history.filter((h) => h.medication_id === filterMed)

  return (
    <div className="stack">
      <button onClick={() => navigate('/more')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← More</button>

      <div className="spread">
        <h1 className="title">Medications</h1>
        {canManage && <button className="btn btn-sm" onClick={() => setForm({})}>+ Add</button>}
      </div>

      {/* Tabs */}
      <div className="row" style={{ background: 'var(--teal-light)', padding: 4, borderRadius: 12, gap: 4 }}>
        {[['today', 'Today'], ['meds', 'List'], ['history', 'History']].map(([v, l]) => (
          <button key={v} className="grow" onClick={() => setTab(v)} style={{ minHeight: 40, border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, background: tab === v ? 'var(--surface)' : 'transparent', color: tab === v ? 'var(--teal-dark)' : 'var(--muted)', boxShadow: tab === v ? 'var(--shadow)' : 'none' }}>{l}</button>
        ))}
      </div>

      {meds.length === 0 && tab !== 'history' ? (
        <EmptyState icon="💊" title="No medications yet" message={canManage ? 'Add the first one to start tracking.' : 'An admin or family member can add medications.'} action={canManage && <button className="btn" onClick={() => setForm({})}>+ Add medication</button>} />
      ) : tab === 'today' ? (
        <>
          <p className="subtitle">{loggedCount} of {meds.length} logged today</p>
          <div className="stack" style={{ gap: 10 }}>
            {meds.map((m) => {
              const refill = refillState(m.refill_reminder_date, today)
              return (
                <div key={m.id} className="card stack" style={{ gap: 10 }}>
                  <div className="spread">
                    <div>
                      <div style={{ fontWeight: 700 }}>{m.name}</div>
                      <div className="muted" style={{ fontSize: 13 }}>{[m.dose, m.timing].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                    {refill && <span className={`badge ${refill.cls}`}>{refill.label}</span>}
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    {STATUSES.map((s) => {
                      const on = todayLogs[m.id] === s.value
                      return (
                        <button key={s.value} className="grow" onClick={() => setStatus(m.id, s.value)} style={{ minHeight: 44, borderRadius: 10, fontWeight: 700, fontSize: 14, border: on ? 'none' : '1.5px solid var(--border)', background: on ? s.btn : 'var(--surface)', color: on ? '#fff' : 'var(--text)' }}>{s.label}</button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : tab === 'meds' ? (
        <div className="stack" style={{ gap: 10 }}>
          {meds.map((m) => {
            const refill = refillState(m.refill_reminder_date, today)
            return (
              <div key={m.id} className="card stack" style={{ gap: 6 }}>
                <div className="spread">
                  <div style={{ fontWeight: 700 }}>{m.name}</div>
                  {refill && <span className={`badge ${refill.cls}`}>{refill.label}</span>}
                </div>
                <div className="muted" style={{ fontSize: 14 }}>{[m.dose, m.timing].filter(Boolean).join(' · ')}</div>
                {m.purpose && <div style={{ fontSize: 14 }}>For {m.purpose}</div>}
                {m.prescribing_doctor && <div className="muted" style={{ fontSize: 13 }}>Prescribed by {m.prescribing_doctor}</div>}
                {m.refill_reminder_date && <div className="muted" style={{ fontSize: 13 }}>Refill: {fmtDate(m.refill_reminder_date)}</div>}
                {canManage && (
                  <div className="row" style={{ gap: 14, marginTop: 4 }}>
                    <button onClick={() => setForm({ medication: m })} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 14, fontWeight: 600, padding: 0 }}>Edit</button>
                    <button onClick={() => deactivate(m)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 14, padding: 0 }}>Deactivate</button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        // History
        <div className="stack" style={{ gap: 10 }}>
          <select className="select" value={filterMed} onChange={(e) => setFilterMed(e.target.value)}>
            <option value="all">All medications</option>
            {meds.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {filteredHistory.length === 0 ? (
            <EmptyState icon="📅" title="No history yet" message="Logged doses will appear here." />
          ) : (
            filteredHistory.map((h) => {
              const s = STATUSES.find((x) => x.value === h.status)
              return (
                <div key={h.id} className="card spread">
                  <div>
                    <div style={{ fontWeight: 600 }}>{h.medication?.name || 'Medication'}</div>
                    <div className="muted" style={{ fontSize: 13 }}>{fmtDate(h.log_date)}</div>
                  </div>
                  <span className={`badge ${s?.cls || ''}`}>{s?.label || h.status}</span>
                </div>
              )
            })
          )}
        </div>
      )}

      {form && (
        <MedicationForm
          recipientId={recipient.id}
          medication={form.medication}
          onCancel={() => setForm(null)}
          onDone={() => { setForm(null); load() }}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
